/**
 * History Service for Search and Investigation persistence
 *
 * This module provides a transparent API for managing user history,
 * abstracting the backend persistence layer. Falls back to localStorage
 * if the backend is unavailable.
 */

import api from '../api';

// Schema version for localStorage fallback
const STORAGE_VERSION = 1;
const STORAGE_KEY = 'vedaya_history';
const MAX_LOCAL_ITEMS = 200;

// Development mode logging
const isDev = process.env.NODE_ENV === 'development';
const log = (...args) => {
  if (isDev) {
    console.log('[HistoryService]', ...args);
  }
};

// ============================================================================
// Local Storage Fallback
// ============================================================================

function getLocalStorage() {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) {
      return { version: STORAGE_VERSION, searches: [], investigations: [], lastUpdated: new Date().toISOString() };
    }
    const data = JSON.parse(stored);
    if (data.version !== STORAGE_VERSION) {
      // Schema migration if needed
      return { version: STORAGE_VERSION, searches: [], investigations: [], lastUpdated: new Date().toISOString() };
    }
    return data;
  } catch (e) {
    console.error('Failed to read localStorage:', e);
    return { version: STORAGE_VERSION, searches: [], investigations: [], lastUpdated: new Date().toISOString() };
  }
}

function setLocalStorage(data) {
  try {
    data.lastUpdated = new Date().toISOString();
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch (e) {
    console.error('Failed to write localStorage:', e);
  }
}

function generateId() {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
}

function pruneLocalStorage(collection, maxItems = MAX_LOCAL_ITEMS) {
  // Keep pinned items, prune oldest non-pinned
  const pinned = collection.filter(item => item.isPinned);
  const nonPinned = collection.filter(item => !item.isPinned);

  // Sort by createdAt descending
  nonPinned.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

  // Keep up to maxItems (pinned + newest non-pinned)
  const maxNonPinned = maxItems - pinned.length;
  return [...pinned, ...nonPinned.slice(0, Math.max(0, maxNonPinned))];
}

// ============================================================================
// API Methods
// ============================================================================

/**
 * List history items
 * @param {Object} params - Query parameters
 * @param {string} [params.type] - 'search' or 'investigation'
 * @param {number} [params.limit] - Max items to return
 * @param {string} [params.cursor] - Pagination cursor
 * @param {string} [params.search] - Filter by query text
 * @param {boolean} [params.pinnedOnly] - Only return pinned items
 * @returns {Promise<{items: Array, total: number, hasMore: boolean, cursor?: string}>}
 */
export async function listHistory(params = {}) {
  try {
    const response = await api.get('/history', {
      params: {
        type: params.type,
        limit: params.limit || 50,
        cursor: params.cursor,
        search: params.search,
        pinned_only: params.pinnedOnly || false,
      },
    });

    log('Fetched history:', response.data.total, 'items');

    return {
      items: response.data.items,
      total: response.data.total,
      hasMore: response.data.has_more,
      cursor: response.data.cursor,
    };
  } catch (error) {
    console.error('Failed to fetch history from API, using localStorage:', error);

    // Fallback to localStorage
    const data = getLocalStorage();
    let items = [];

    if (!params.type || params.type === 'search') {
      items = [...items, ...data.searches.map(s => ({ ...s, _type: 'search' }))];
    }
    if (!params.type || params.type === 'investigation') {
      items = [...items, ...data.investigations.map(i => ({ ...i, _type: 'investigation' }))];
    }

    // Apply filters
    if (params.search) {
      const searchLower = params.search.toLowerCase();
      items = items.filter(item => {
        const text = item.queryText || item.title || '';
        return text.toLowerCase().includes(searchLower);
      });
    }

    if (params.pinnedOnly) {
      items = items.filter(item => item.isPinned);
    }

    // Sort by createdAt descending
    items.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

    // Apply limit
    const limit = params.limit || 50;
    const hasMore = items.length > limit;
    items = items.slice(0, limit);

    return {
      items,
      total: items.length,
      hasMore,
      cursor: null,
    };
  }
}

/**
 * Get history counts
 * @returns {Promise<{search: number, investigation: number, total: number}>}
 */
export async function getHistoryCount() {
  try {
    const response = await api.get('/history/count');
    return response.data;
  } catch (error) {
    // Fallback to localStorage
    const data = getLocalStorage();
    return {
      search: data.searches.length,
      investigation: data.investigations.length,
      total: data.searches.length + data.investigations.length,
    };
  }
}

// ============================================================================
// Search History
// ============================================================================

/**
 * Create a search history item
 * @param {Object} data - Search data
 * @param {string} data.queryText - The search query
 * @param {Object} [data.filters] - Applied filters
 * @param {string} [data.dataSource] - Data source/index
 * @returns {Promise<{id: string, item: Object}>}
 */
export async function createSearchHistory(data) {
  try {
    const response = await api.post('/history/search', {
      query_text: data.queryText,
      filters: data.filters,
      data_source: data.dataSource,
    });

    log('Created search history:', response.data.id);
    return { id: response.data.id, item: response.data.item };
  } catch (error) {
    console.error('Failed to create search history in API, using localStorage:', error);

    // Fallback to localStorage
    const storage = getLocalStorage();
    const item = {
      id: generateId(),
      createdAt: new Date().toISOString(),
      queryText: data.queryText,
      filters: data.filters || { sources: [] },
      dataSource: data.dataSource,
      isPinned: false,
    };

    storage.searches.unshift(item);
    storage.searches = pruneLocalStorage(storage.searches);
    setLocalStorage(storage);

    log('Created search history (local):', item.id);
    return { id: item.id, item };
  }
}

/**
 * Update search history with results metadata
 * @param {string} historyId - History item ID
 * @param {Object} data - Update data
 * @param {Object} [data.resultMeta] - Result metadata
 * @param {string} [data.linkedInvestigationId] - Linked investigation ID
 * @returns {Promise<{updated: boolean, item?: Object}>}
 */
export async function updateSearchHistory(historyId, data) {
  try {
    const response = await api.patch(`/history/search/${historyId}`, {
      result_meta: data.resultMeta,
      linked_investigation_id: data.linkedInvestigationId,
    });

    log('Updated search history:', historyId);
    return { updated: true, item: response.data.item };
  } catch (error) {
    console.error('Failed to update search history:', error);

    // Fallback to localStorage
    const storage = getLocalStorage();
    const index = storage.searches.findIndex(s => s.id === historyId);
    if (index !== -1) {
      if (data.resultMeta) {
        storage.searches[index].resultMeta = data.resultMeta;
      }
      if (data.linkedInvestigationId) {
        storage.searches[index].linkedInvestigationId = data.linkedInvestigationId;
      }
      setLocalStorage(storage);
      return { updated: true, item: storage.searches[index] };
    }

    return { updated: false };
  }
}

/**
 * Get a single search history item
 * @param {string} historyId - History item ID
 * @returns {Promise<Object|null>}
 */
export async function getSearchHistoryItem(historyId) {
  try {
    const response = await api.get(`/history/search/${historyId}`);
    return response.data;
  } catch (error) {
    // Fallback to localStorage
    const storage = getLocalStorage();
    return storage.searches.find(s => s.id === historyId) || null;
  }
}

/**
 * Delete a search history item
 * @param {string} historyId - History item ID
 * @returns {Promise<{deleted: boolean}>}
 */
export async function deleteSearchHistory(historyId) {
  try {
    await api.delete(`/history/search/${historyId}`);
    log('Deleted search history:', historyId);
    return { deleted: true };
  } catch (error) {
    console.error('Failed to delete search history:', error);

    // Fallback to localStorage
    const storage = getLocalStorage();
    const index = storage.searches.findIndex(s => s.id === historyId);
    if (index !== -1) {
      storage.searches.splice(index, 1);
      setLocalStorage(storage);
      return { deleted: true };
    }

    return { deleted: false };
  }
}

// ============================================================================
// Investigation History
// ============================================================================

/**
 * Create an investigation history item
 * @param {Object} data - Investigation data
 * @param {string} data.title - Investigation title
 * @param {Object} data.input - Investigation input context
 * @param {Object} [data.restorationData] - Data needed to restore the investigation
 * @returns {Promise<{id: string, item: Object}>}
 */
export async function createInvestigationHistory(data) {
  try {
    const response = await api.post('/history/investigation', {
      title: data.title,
      input: data.input,
      restoration_data: data.restorationData,
    });

    log('Created investigation history:', response.data.id);
    return { id: response.data.id, item: response.data.item };
  } catch (error) {
    console.error('Failed to create investigation history:', error);

    // Fallback to localStorage
    const storage = getLocalStorage();
    const item = {
      id: generateId(),
      createdAt: new Date().toISOString(),
      title: data.title,
      input: data.input,
      status: 'inProgress',
      restorationData: data.restorationData,
      isPinned: false,
    };

    storage.investigations.unshift(item);
    storage.investigations = pruneLocalStorage(storage.investigations);
    setLocalStorage(storage);

    log('Created investigation history (local):', item.id);
    return { id: item.id, item };
  }
}

/**
 * Update investigation history
 * @param {string} historyId - History item ID
 * @param {Object} data - Update data
 * @param {string} [data.status] - New status
 * @param {Object} [data.stepsMeta] - Steps metadata
 * @param {Object} [data.outputsMeta] - Outputs metadata
 * @param {string} [data.completedAt] - Completion timestamp
 * @param {Object} [data.restorationData] - Updated restoration data
 * @returns {Promise<{updated: boolean, item?: Object}>}
 */
export async function updateInvestigationHistory(historyId, data) {
  try {
    const response = await api.patch(`/history/investigation/${historyId}`, {
      status: data.status,
      steps_meta: data.stepsMeta,
      outputs_meta: data.outputsMeta,
      completed_at: data.completedAt,
      restoration_data: data.restorationData,
    });

    log('Updated investigation history:', historyId);
    return { updated: true, item: response.data.item };
  } catch (error) {
    console.error('Failed to update investigation history:', error);

    // Fallback to localStorage
    const storage = getLocalStorage();
    const index = storage.investigations.findIndex(i => i.id === historyId);
    if (index !== -1) {
      const item = storage.investigations[index];
      if (data.status) item.status = data.status;
      if (data.stepsMeta) item.stepsMeta = data.stepsMeta;
      if (data.outputsMeta) item.outputsMeta = data.outputsMeta;
      if (data.completedAt) item.completedAt = data.completedAt;
      if (data.restorationData) item.restorationData = data.restorationData;
      setLocalStorage(storage);
      return { updated: true, item };
    }

    return { updated: false };
  }
}

/**
 * Get a single investigation history item
 * @param {string} historyId - History item ID
 * @returns {Promise<Object|null>}
 */
export async function getInvestigationHistoryItem(historyId) {
  try {
    const response = await api.get(`/history/investigation/${historyId}`);
    return response.data;
  } catch (error) {
    // Fallback to localStorage
    const storage = getLocalStorage();
    return storage.investigations.find(i => i.id === historyId) || null;
  }
}

/**
 * Delete an investigation history item
 * @param {string} historyId - History item ID
 * @returns {Promise<{deleted: boolean}>}
 */
export async function deleteInvestigationHistory(historyId) {
  try {
    await api.delete(`/history/investigation/${historyId}`);
    log('Deleted investigation history:', historyId);
    return { deleted: true };
  } catch (error) {
    console.error('Failed to delete investigation history:', error);

    // Fallback to localStorage
    const storage = getLocalStorage();
    const index = storage.investigations.findIndex(i => i.id === historyId);
    if (index !== -1) {
      storage.investigations.splice(index, 1);
      setLocalStorage(storage);
      return { deleted: true };
    }

    return { deleted: false };
  }
}

// ============================================================================
// Generic Operations (pin, rename, delete)
// ============================================================================

/**
 * Update a history item (pin, rename)
 * @param {string} historyId - History item ID
 * @param {string} itemType - 'search' or 'investigation'
 * @param {Object} data - Update data
 * @param {string} [data.customTitle] - Custom title
 * @param {boolean} [data.isPinned] - Pin status
 * @returns {Promise<{updated: boolean}>}
 */
export async function updateHistoryItem(historyId, itemType, data) {
  try {
    await api.patch(`/history/${historyId}`, {
      custom_title: data.customTitle,
      is_pinned: data.isPinned,
    }, {
      params: { item_type: itemType },
    });

    log('Updated history item:', historyId, data);
    return { updated: true };
  } catch (error) {
    console.error('Failed to update history item:', error);

    // Fallback to localStorage
    const storage = getLocalStorage();
    const collection = itemType === 'search' ? storage.searches : storage.investigations;
    const item = collection.find(i => i.id === historyId);

    if (item) {
      if (data.customTitle !== undefined) item.customTitle = data.customTitle;
      if (data.isPinned !== undefined) item.isPinned = data.isPinned;
      setLocalStorage(storage);
      return { updated: true };
    }

    return { updated: false };
  }
}

/**
 * Delete a history item (auto-detects type)
 * @param {string} historyId - History item ID
 * @returns {Promise<{deleted: boolean}>}
 */
export async function deleteHistoryItem(historyId) {
  try {
    await api.delete(`/history/${historyId}`);
    log('Deleted history item:', historyId);
    return { deleted: true };
  } catch (error) {
    console.error('Failed to delete history item:', error);

    // Fallback: try both collections
    let deleted = (await deleteSearchHistory(historyId)).deleted;
    if (!deleted) {
      deleted = (await deleteInvestigationHistory(historyId)).deleted;
    }

    return { deleted };
  }
}

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Format a history item for display
 * @param {Object} item - History item
 * @returns {Object} Formatted item
 */
export function formatHistoryItem(item) {
  const isSearch = item._type === 'search' || item.queryText !== undefined || item.query_text !== undefined;
  const title = item.customTitle || item.custom_title || (isSearch ? (item.queryText || item.query_text) : item.title) || 'Untitled';
  const createdAt = new Date(item.createdAt || item.created_at);

  return {
    ...item,
    _type: isSearch ? 'search' : 'investigation',
    displayTitle: title.length > 60 ? title.substring(0, 57) + '...' : title,
    formattedDate: formatRelativeTime(createdAt),
    timestamp: createdAt.toISOString(),
    createdAt: createdAt.toISOString(), // Standardize for grouping
    // Normalize core fields
    queryText: item.queryText || item.query_text,
    id: item.id || item._id,
  };
}

/**
 * Format a date as relative time
 * @param {Date} date - Date to format
 * @returns {string} Relative time string
 */
export function formatRelativeTime(date) {
  const now = new Date();
  const diff = Math.floor((now - date) / 1000);

  if (diff < 60) return 'Just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;

  return date.toLocaleDateString();
}

/**
 * Clear all local history (for testing/debugging)
 */
export function clearLocalHistory() {
  localStorage.removeItem(STORAGE_KEY);
  log('Cleared local history');
}

export default {
  listHistory,
  getHistoryCount,
  createSearchHistory,
  updateSearchHistory,
  getSearchHistoryItem,
  deleteSearchHistory,
  createInvestigationHistory,
  updateInvestigationHistory,
  getInvestigationHistoryItem,
  deleteInvestigationHistory,
  updateHistoryItem,
  deleteHistoryItem,
  formatHistoryItem,
  formatRelativeTime,
  clearLocalHistory,
};
